import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ENG_PRIMARY } from './constants';
import { getEngInitials } from './helpers';

// ── Logo / back-button area ───────────────────────────────────────────────────
export function EngLogoArea({ config, defaultLogoSrc = '', isBoothLobby, backLabel, onBack, onLogoClick }) {
  const showLogo = isBoothLobby ? false : config.showLogo !== false;
  // Visibility is fully controlled by the caller via onBack — null means hide
  const showBack = !!onBack;
  const effectiveLogoSrc = config.logoSrc || defaultLogoSrc;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 32 }}>
      {showLogo && (
        effectiveLogoSrc ? (
          <Box
            onClick={onLogoClick}
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...(onLogoClick ? { cursor: 'pointer' } : {}) }}
          >
            <Box component="img" src={effectiveLogoSrc} alt={config.logoAlt || 'Logo'}
              sx={{ height: config.logoHeight || 36, width: 'auto', display: 'block', objectFit: 'contain' }} />
          </Box>
        ) : (
          <Box
            onClick={onLogoClick}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ...(onLogoClick ? { cursor: 'pointer' } : {}) }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: alpha(ENG_PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: ENG_PRIMARY }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: ENG_PRIMARY, letterSpacing: '-0.5px' }}>
              {config.logoText || 'Event'}
            </Typography>
          </Box>
        )
      )}
      {showBack && (
        <Box onClick={onBack}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, cursor: 'pointer', borderRadius: '8px', px: 1.25, py: 0.6, background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)', transition: 'all 0.15s', '&:hover': { background: 'rgba(0,0,0,0.12)' } }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill={ENG_PRIMARY}>
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: ENG_PRIMARY, whiteSpace: 'nowrap' }}>
            {'Back'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Single nav link ───────────────────────────────────────────────────────────
export function EngNavLink({ link, color, onNavigateLobby, currentLobbyId, mobile = false }) {
  const isActive = !!(link.lobbyId && link.lobbyId === currentLobbyId);

  const handleClick = e => {
    e.stopPropagation();
    if (link.lobbyId && onNavigateLobby) {
      if (link.lobbyId !== currentLobbyId) onNavigateLobby(link.lobbyId);
    } else if (link.href) {
      window.open(link.href, link.newTab ? '_blank' : '_self', 'noopener,noreferrer');
    }
  };

  if (mobile) {
    return (
      <Box onClick={handleClick} sx={{
        py: 1.1, px: 0.5,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'center', gap: 1,
        cursor: 'pointer',
        '&:hover': { background: 'rgba(0,0,0,0.03)' },
      }}>
        {isActive && <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: ENG_PRIMARY, flexShrink: 0 }} />}
        <Typography sx={{ fontSize: '0.95rem', fontWeight: isActive ? 700 : 500, color: isActive ? ENG_PRIMARY : (color || '#1a1a2e') }}>
          {link.label || 'Link'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box onClick={handleClick}
      sx={{ px: 1.25, py: 0.5, borderRadius: '7px', display: 'inline-flex', alignItems: 'center', cursor: isActive ? 'default' : 'pointer', whiteSpace: 'nowrap', background: isActive ? alpha(ENG_PRIMARY, 0.12) : 'transparent', border: `1px solid ${isActive ? alpha(ENG_PRIMARY, 0.25) : 'transparent'}`, transition: 'all 0.15s', '&:hover': isActive ? {} : { background: alpha(color || ENG_PRIMARY, 0.08), border: `1px solid ${alpha(color || ENG_PRIMARY, 0.2)}` } }}
    >
      {isActive && <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: ENG_PRIMARY, mr: 0.75, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: '0.875rem', fontWeight: isActive ? 700 : 500, color: isActive ? ENG_PRIMARY : (color || '#1a1a2e'), lineHeight: 1 }}>
        {link.label || 'Link'}
      </Typography>
    </Box>
  );
}

// ── Chat channel dropdown button (desktop header) ─────────────────────────────
export function EngChatButton({ chatHotspots, onOpenChat }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open]);

  if (!chatHotspots?.length) return null;

  return (
    <Box ref={ref} sx={{ position: 'relative', flexShrink: 0 }}>
      <Box onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        sx={{ width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: open ? alpha(ENG_PRIMARY, 0.12) : 'transparent', border: `1px solid ${open ? alpha(ENG_PRIMARY, 0.3) : alpha('#000', 0.10)}`, transition: 'all 0.15s', '&:hover': { background: alpha(ENG_PRIMARY, 0.1), borderColor: alpha(ENG_PRIMARY, 0.3) } }}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill={open ? ENG_PRIMARY : '#5a6474'}>
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
        {chatHotspots.length > 0 && (
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: 8, height: 8, borderRadius: '50%', background: ENG_PRIMARY, border: '1.5px solid #fff' }} />
        )}
      </Box>
      {open && (
        <Box sx={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff', border: '1px solid #e4e9f0', borderRadius: '12px', boxShadow: '0 8px 28px rgba(0,0,0,0.13)', minWidth: 220, py: '6px', zIndex: 9999, overflow: 'hidden' }}>
          <Typography sx={{ px: 1.5, pb: 0.75, pt: 0.25, fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Chat channels</Typography>
          {chatHotspots.map(h => (
            <Box key={h.id} onClick={e => { e.stopPropagation(); onOpenChat(h); setOpen(false); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, cursor: 'pointer', transition: 'background 0.12s', '&:hover': { background: '#f5f7fa' } }}
            >
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', background: alpha(ENG_PRIMARY, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill={ENG_PRIMARY}>
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
              </Box>
              <Typography sx={{ fontSize: '0.83rem', fontWeight: 500, color: '#1a1a2e' }}>
                {h.hotspotAction?.chatTitle || h.label || 'Live Chat'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Account / logout button (desktop header) ──────────────────────────────────
export function EngAccountButton({ config, participantName, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref   = useRef(null);
  const bg    = config.style?.accountBg    || ENG_PRIMARY;
  const color = config.style?.accountColor || '#fff';
  const name  = participantName || config.accountLabel || '';
  const initials = getEngInitials(name);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open]);

  return (
    <Box ref={ref} sx={{ position: 'relative', flexShrink: 0 }}>
      <Box onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        sx={{ width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: open ? `0 0 0 3px ${alpha(bg, 0.3)}` : 'none', transition: 'box-shadow 0.15s', '&:hover': { filter: 'brightness(0.92)' } }}
      >
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color, lineHeight: 1, letterSpacing: '0.02em' }}>
          {initials}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff', border: '1px solid #e4e9f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 140, py: '4px', zIndex: 9999, overflow: 'hidden' }}>
          {name && (
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f0f4f8' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a1a2e' }}>{name}</Typography>
            </Box>
          )}
          <Box onClick={e => { e.stopPropagation(); setOpen(false); onLogout?.(); }}
            sx={{ px: 2, py: 1.25, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#dc2626', transition: 'background 0.12s', '&:hover': { background: '#fff5f5' } }}
          >
            Logout
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── EngMobileDrawerOverlay — rendered at wrapperRef level in LobbyEngine ──────
// Uses position:absolute scoped to wrapperRef (position:relative, no overflow clip).
// This keeps the drawer inside the lobby canvas without escaping to the browser.
export function EngMobileDrawerOverlay({ open, onClose, config, defaultLogoSrc = '', onLogoClick, onNavigateLobby, currentLobbyId, chatHotspots, onOpenChat, participantName, onLogout }) {
  const links     = config.links || [];
  const bg        = config.style?.background || '#fff';
  const linkColor = config.style?.linkColor;
  const accountBg = config.style?.accountBg || ENG_PRIMARY;
  const name      = participantName || config.accountLabel || '';

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.45)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'all' : 'none',
          transition: 'opacity 0.22s ease',
        }}
      />

      {/* Drawer panel */}
      <Box sx={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: 260,
        background: bg,
        zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.22)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
        borderLeft: '1px solid rgba(0,0,0,0.07)',
      }}>

        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
          <EngLogoArea config={config} defaultLogoSrc={defaultLogoSrc} onLogoClick={onLogoClick ? () => { onLogoClick(); onClose(); } : undefined} />
          <Box onClick={onClose}
            sx={{ width: 32, height: 32, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.1)', color: '#5a6474', transition: 'all 0.15s', '&:hover': { background: 'rgba(0,0,0,0.05)' } }}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Nav links */}
          {links.length > 0 && (
            <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>
                Navigation
              </Typography>
              {links.map((link, i) => (
                <EngNavLink key={link.id || i} link={link} color={linkColor} mobile
                  onNavigateLobby={(id) => { onNavigateLobby?.(id); onClose(); }}
                  currentLobbyId={currentLobbyId}
                />
              ))}
            </Box>
          )}

          {/* Chat channels list */}
          {chatHotspots?.length > 0 && (
            <Box sx={{ px: 2.5, pt: links.length > 0 ? 2 : 1.5, pb: 0.5 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
                Chat Channels
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {chatHotspots.map(h => (
                  <Box key={h.id}
                    onClick={e => { e.stopPropagation(); onOpenChat(h); onClose(); }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '10px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.07)', background: 'rgba(0,0,0,0.02)', transition: 'background 0.12s', '&:hover': { background: alpha(ENG_PRIMARY, 0.06), borderColor: alpha(ENG_PRIMARY, 0.2) } }}
                  >
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: alpha(ENG_PRIMARY, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg viewBox="0 0 24 24" width="15" height="15" fill={ENG_PRIMARY}>
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                      </svg>
                    </Box>
                    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.hotspotAction?.chatTitle || h.label || 'Live Chat'}
                    </Typography>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="#c4cdd8">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                    </svg>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer: name + full-width logout button */}
        <Box sx={{ px: 2.5, py: 2.5, borderTop: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
          {name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: accountBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: config.style?.accountColor || '#fff', lineHeight: 1 }}>
                  {getEngInitials(name)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', lineHeight: 1, mb: 0.3 }}>Signed in as</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            onClick={e => { e.stopPropagation(); onLogout?.(); onClose(); }}
            sx={{ width: '100%', py: 1.25, borderRadius: '10px', background: '#fff1f2', border: '1.5px solid #fecdd3', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { background: '#ffe4e6', borderColor: '#fda4af' } }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#dc2626">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#dc2626' }}>Logout</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ── useNavOverflow ────────────────────────────────────────────────────────────
function useNavOverflow(navRef, links) {
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [links]);
  return overflows;
}

// ── Right-side button cluster (desktop) ───────────────────────────────────────
function EngRightButtons({ config, participantName, onLogout, chatHotspots, onOpenChat }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <EngChatButton chatHotspots={chatHotspots} onOpenChat={onOpenChat} />
      <EngAccountButton config={config} participantName={participantName} onLogout={onLogout} />
    </Box>
  );
}

// ── Classic (full-width bar) header ──────────────────────────────────────────
function EngClassicHeader(props) {
  const {
    config, defaultLogoSrc, participantName, onLogout, onNavigateLobby, currentLobbyId,
    chatHotspots, onOpenChat, isBoothLobby, backLabel, onBack, isMobile, onOpenDrawer, onLogoClick,
  } = props;
  const links    = config.links || [];
  const navRef   = useRef(null);
  const overflows = useNavOverflow(navRef, links);
  const showBurger = isMobile || overflows;

  const px = isMobile
    ? (config.style?.mobilePaddingX || '16px')
    : (config.style?.paddingX || '48px');

  return (
    <Box sx={{ width: '100%', background: config.style?.background || '#fff', borderBottom: config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`, px, height: config.style?.height || 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, boxSizing: 'border-box', position: 'relative' }}>
      <EngLogoArea config={config} defaultLogoSrc={defaultLogoSrc} isBoothLobby={isBoothLobby} backLabel={backLabel} onBack={onBack} onLogoClick={onLogoClick} />

      {!showBurger && (
        <Box ref={navRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
          {links.map((link, i) => (
            <EngNavLink key={link.id || i} link={link} color={config.style?.linkColor} onNavigateLobby={onNavigateLobby} currentLobbyId={currentLobbyId} />
          ))}
        </Box>
      )}
      {!isMobile && (
        <Box ref={navRef} aria-hidden sx={{ display: showBurger ? 'flex' : 'none', position: 'absolute', visibility: 'hidden', pointerEvents: 'none', gap: 0.5, alignItems: 'center', flex: 1 }}>
          {links.map((link, i) => (
            <EngNavLink key={link.id || i} link={link} color={config.style?.linkColor} onNavigateLobby={onNavigateLobby} currentLobbyId={currentLobbyId} />
          ))}
        </Box>
      )}

      {showBurger ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {!isMobile && <EngChatButton chatHotspots={chatHotspots} onOpenChat={onOpenChat} />}
          <Box onClick={e => { e.stopPropagation(); onOpenDrawer?.(); }}
            sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.12)', transition: 'all 0.15s', '&:hover': { background: alpha(ENG_PRIMARY, 0.06), borderColor: alpha(ENG_PRIMARY, 0.3) } }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#5a6474">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </Box>
        </Box>
      ) : (
        <EngRightButtons config={config} participantName={participantName} onLogout={onLogout} chatHotspots={chatHotspots} onOpenChat={onOpenChat} />
      )}
    </Box>
  );
}

// ── Pill (floating capsule) header ────────────────────────────────────────────
function EngPillHeader(props) {
  const {
    config, defaultLogoSrc, participantName, onLogout, onNavigateLobby, currentLobbyId,
    chatHotspots, onOpenChat, isBoothLobby, backLabel, onBack, isMobile, onOpenDrawer, onLogoClick,
  } = props;
  const links      = config.links || [];
  const navRef     = useRef(null);
  const overflows  = useNavOverflow(navRef, links);
  const showBurger = isMobile || overflows;

  const paddingX  = isMobile
    ? (config.style?.mobilePaddingX || '12px')
    : (config.style?.paddingX || '40px');
  const maxWidth  = config.style?.pillMaxWidth || 1100;
  const topOffset = config.style?.pillTop ?? 16;

  return (
    <Box sx={{ width: '100%', px: paddingX, pt: `${topOffset + 8}px`, pb: isMobile ? 0 : `${topOffset + 8}px`, display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: config.style?.pillBg || '#fff', borderRadius: '999px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', px: isMobile ? 1.5 : 2.5, height: 56, gap: 2, width: '100%', maxWidth, boxSizing: 'border-box', border: `1px solid ${alpha('#000', 0.07)}`, position: 'relative', overflow: 'visible' }}>
        <EngLogoArea config={config} defaultLogoSrc={defaultLogoSrc} isBoothLobby={isBoothLobby} backLabel={backLabel} onBack={onBack} onLogoClick={onLogoClick} />

        {!showBurger && (
          <Box ref={navRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
            {links.map((link, i) => <EngNavLink key={link.id || i} link={link} color={config.style?.linkColor} onNavigateLobby={onNavigateLobby} currentLobbyId={currentLobbyId} />)}
          </Box>
        )}
        {!isMobile && (
          <Box ref={navRef} aria-hidden sx={{ display: showBurger ? 'flex' : 'none', position: 'absolute', visibility: 'hidden', pointerEvents: 'none', gap: 0.5, alignItems: 'center', flex: 1 }}>
            {links.map((link, i) => <EngNavLink key={link.id || i} link={link} color={config.style?.linkColor} onNavigateLobby={onNavigateLobby} currentLobbyId={currentLobbyId} />)}
          </Box>
        )}

        {showBurger ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {!isMobile && <EngChatButton chatHotspots={chatHotspots} onOpenChat={onOpenChat} />}
            <Box onClick={e => { e.stopPropagation(); onOpenDrawer?.(); }}
              sx={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.12)', transition: 'all 0.15s', '&:hover': { background: alpha(ENG_PRIMARY, 0.06), borderColor: alpha(ENG_PRIMARY, 0.3) } }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#5a6474">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
              </svg>
            </Box>
          </Box>
        ) : (
          <EngRightButtons config={config} participantName={participantName} onLogout={onLogout} chatHotspots={chatHotspots} onOpenChat={onOpenChat} />
        )}
      </Box>
    </Box>
  );
}

// ── Public entry point ────────────────────────────────────────────────────────
export function EngineHeader(props) {
  const { config, isMobile } = props;
  if (config == null) return null;
  const isPill = (config.template || 'classic') === 'pill';
  return isPill
    ? <EngPillHeader    {...props} isMobile={isMobile} />
    : <EngClassicHeader {...props} isMobile={isMobile} />;
}